import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import SearchResultMoreActionsDropdown from 'components/Dropdowns/SearchResultMoreActionsDropdown/SearchResultMoreActionsDropdown'
import { SearchCategory } from 'constants/enums'
import { formatSeconds } from 'utils/stringHelpers'
import { placeholderLogo } from 'images'
import labelPlaceholderLogo from 'images/icons/labelPlaceholder.png'

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
`

const ThumbWrap = styled.div`
  position: relative;
  width: 48px;
  height: 48px;
  flex: none;
  border-radius: 8px;
  overflow: hidden;
`

const Thumb = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const Main = styled.div`
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  min-width: 0;
`

const Texts = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1 1 auto;
`

const Title = styled.div`
  color: ${({ theme }) => theme.colors.neutralW100};
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.colors.neutralW70};
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: 8px;
`

const Duration = styled.span`
  color: ${({ theme }) => theme.colors.neutralW80};
  font-size: 12px;
  flex: none;
`

const ExplicitBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 3px;
  border: 1px solid ${({ theme }) => theme.colors.neutralW60};
  color: ${({ theme }) => theme.colors.neutralW80};
  font-size: 10px;
  line-height: 1;
`

function getThumbSrc(type, item) {
  if (type === SearchCategory.artists)
    return item.thumbnail || labelPlaceholderLogo
  if (type === SearchCategory.labels) return item.logo || labelPlaceholderLogo
  return item.coverart || placeholderLogo
}

function getTitleText(type, item) {
  if (type === SearchCategory.artists) return item.artist
  if (type === SearchCategory.labels) return item.label
  return item.title || item.release || ''
}

function getArtistText(type, item) {
  if (type === SearchCategory.artists) return ''
  if (type === SearchCategory.labels) return ''
  return item.artist || ''
}

function getDuration(type, item) {
  const seconds = item?.duration
  if (
    !seconds ||
    (type !== SearchCategory.tracks && type !== SearchCategory.releases)
  )
    return null
  return formatSeconds(seconds)
}

const ResultRowMobile = ({
  type,
  item,
  onShowModal,
  onPlayRelease,
  onDownloadRelease,
  onPlayTrack,
  onDownloadTrack
}) => {
  const title = getTitleText(type, item)
  const artist = getArtistText(type, item)
  const explicit = !!item?.explicit
  const duration = getDuration(type, item)
  const thumb = getThumbSrc(type, item)
  const hasMenu =
    type === SearchCategory.tracks || type === SearchCategory.releases

  return (
    <Row role="button">
      <ThumbWrap>
        <Thumb src={thumb} alt={title} />
      </ThumbWrap>

      <Main>
        <Texts>
          <Title>{title}</Title>
          <Meta>
            {explicit && <ExplicitBadge>E</ExplicitBadge>}
            {artist && <span>{artist}</span>}
          </Meta>
        </Texts>

        <Right>
          {duration && <Duration>{duration}</Duration>}
          {hasMenu && (
            <SearchResultMoreActionsDropdown
              type={type}
              item={item}
              onShowModal={onShowModal}
              onPlayRelease={onPlayRelease}
              onDownloadRelease={onDownloadRelease}
              onPlayTrack={onPlayTrack}
              onDownloadTrack={onDownloadTrack}
            />
          )}
        </Right>
      </Main>
    </Row>
  )
}

ResultRowMobile.propTypes = {
  type: PropTypes.oneOf([
    SearchCategory.tracks,
    SearchCategory.releases,
    SearchCategory.artists,
    SearchCategory.labels
  ]).isRequired,
  item: PropTypes.object.isRequired,
  onShowModal: PropTypes.func,
  onPlayRelease: PropTypes.func,
  onDownloadRelease: PropTypes.func,
  onPlayTrack: PropTypes.func,
  onDownloadTrack: PropTypes.func
}

export default ResultRowMobile
